/**
 * Forbidden property-name patterns for PostHog Event Properties.
 *
 * Mirrors the backend wrapper at ``market-intelligence_backend/
 * src/mi/observability/posthog.py`` ``FORBIDDEN_PROPERTY_PATTERNS``
 * plus the four extra Plan_05 entries (``address``, ``vat_id``,
 * ``legal_name``, ``registry_id``) called out in master decision #25.
 * The two lists must stay aligned — any addition to one half belongs
 * in the other.
 *
 * Patterns ending in ``*`` are glob-style: ``password_*`` matches
 * ``password_hash``, ``password_v2``, etc. (does NOT match the bare
 * word ``password`` — list it separately if you need both).
 *
 * "email", "name", "full_name" are forbidden as Event Properties but
 * are explicitly allowed as Person Properties (set via ``$identify``).
 * The wrapper enforces the distinction by running this check on the
 * event-property payload only.
 *
 * Plan-doc anchors
 * ----------------
 * - ``plans/PLAN_POSTHOG_02_EVENT_CATALOG.md`` § "Forbidden properties"
 * - ``plans/PLAN_POSTHOG_05_GOVERNANCE.md`` § "Forbidden property names"
 * - Master decision #25 — backend list missed the 4 commercially-
 *   sensitive entries; this frontend file ships the full union.
 */

export const FORBIDDEN_PROPERTY_PATTERNS = [
  // Identity (live on Person via $identify, never on Event)
  "email",
  "name",
  "full_name",
  "phone",
  // Commercially sensitive (Plan_05; not in backend list as of PR-1)
  "address",
  "vat_id",
  "legal_name",
  "registry_id",
  // Credentials
  "password",
  "password_hash",
  "password_*",
  "mfa_secret",
  "mfa_secret_*",
  "token",
  "access_token",
  "refresh_token",
  "csrf_token",
  "jwt",
  "jwt_*",
  // Network / geo precision (PostHog auto-redacts $ip)
  "ip",
  "ip_address",
  "geo_lat",
  "geo_lng",
  "geo_coordinates",
  // Session secrets
  "session_token",
  "session_secret",
  // Commercially sensitive (single-key)
  "master_contract_pdf_sha256",
  // Stack-trace bodies — these belong in captureException(), not
  // event property bags. Hard rule per Plan_03 § "Backend redaction
  // policy" (frontend mirrors).
  "stack",
  "traceback",
  "exc_info",
  "error_stack",
] as const;

export type ForbiddenPattern = (typeof FORBIDDEN_PROPERTY_PATTERNS)[number];

/**
 * Returns true if ``key`` matches any forbidden pattern. Supports
 * trailing-``*`` glob style — ``password_*`` matches anything starting
 * with ``password_``. Bare names match exactly.
 */
export function isForbiddenProperty(key: string): boolean {
  for (const pattern of FORBIDDEN_PROPERTY_PATTERNS) {
    if (pattern.endsWith("*")) {
      const prefix = pattern.slice(0, -1);
      if (key.startsWith(prefix)) return true;
    } else if (pattern === key) {
      return true;
    }
  }
  return false;
}

/**
 * Identity property names that ARE allowed inside the person/group
 * property bags (``$set`` / ``$set_once`` / ``$group_set`` /
 * ``$group_set_once``) even though they are forbidden as Event
 * Properties. This is the allowlist half of the contract documented at
 * the top of this file: identity fields live on the Person (or Group)
 * via ``$identify`` / ``$groupidentify``, never on an Event.
 *
 * The ``redactEvent`` ``before_send`` hook consults this so it stops
 * stripping ``email`` / ``name`` / ``full_name`` out of person/group
 * property bags — the previous behaviour ran the full event-property
 * forbidden filter over ``$set`` too, which silently emptied every
 * "Set person properties" / "Group identify" payload (the person was
 * left as a bare distinct_id with no email or name, the company group
 * with no name). Secrets (``password_*``, ``token``, ``mfa_secret_*``,
 * ``jwt_*``, ``session_*``, stack bodies, …) are NOT on this list and
 * stay stripped from person/group bags too — defence-in-depth holds.
 *
 * Mirrors the documented trio at the top of this file; widen
 * deliberately (e.g. add ``phone``) only when a person-property is
 * actually set by the bridge AND cleared for PostHog storage.
 */
export const PERSON_PROPERTY_ALLOWED_NAMES = [
  "email",
  "name",
  "full_name",
] as const;

/** True if ``key`` is permitted inside a person/group property bag. */
export function isPersonPropertyAllowed(key: string): boolean {
  return (PERSON_PROPERTY_ALLOWED_NAMES as readonly string[]).includes(key);
}
